import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Facebook, Send, Download, Database, FormInput, AlertTriangle, Link2, Unlink, RefreshCw,
  Zap, Plus, Pencil, Archive, History, CheckCircle2, XCircle,
} from "lucide-react";
import { FieldMapper } from "@/components/FieldMapper";
import { WebinarSourceBinding } from "@/components/webinar/WebinarSourceBinding";

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 py-1.5"><span className="text-sm">{label}</span>{children}</div>
);

/* Toggleable badge list of every SalesOS Product currently assigned to a
   Dataset — a Dataset may now serve MORE than one Product (Section 20).
   Saves through the same dataset PATCH every other dataset edit already
   uses; product_codes is additive on the backend (never wipes the legacy
   single product_code field). */
const DatasetProductsEditor = ({ dataset, products, onSaved }) => {
  const assigned = new Set([
    ...(dataset.product_codes || []),
    ...(dataset.product_code ? [dataset.product_code] : []),
  ]);
  const [busy, setBusy] = useState(false);
  const toggle = async (code) => {
    const next = new Set(assigned);
    next.has(code) ? next.delete(code) : next.add(code);
    setBusy(true);
    try {
      const { data } = await api.patch(`/meta/datasets/${dataset.dataset_key}`, {
        name: dataset.name, dataset_id: dataset.dataset_id,
        product_code: dataset.product_code || null,
        product_codes: Array.from(next),
        enabled: dataset.enabled !== false, test_mode: dataset.test_mode !== false,
        test_event_code: dataset.test_event_code || null,
      });
      if (data.warnings?.length) {
        toast.warning(`${data.warnings.length} Conversion Rule(s) now reference a product no `
          + "longer assigned to this dataset — review them.",
          { description: data.warnings.map((w) => w.rule_name).join(", ") });
      } else {
        toast.success("Assigned products updated");
      }
      onSaved();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Label className="text-[10px] text-muted-foreground shrink-0">Assigned products</Label>
      {products.map((p) => (
        <button key={p.code} type="button" disabled={busy} data-testid={`dataset-product-toggle-${dataset.dataset_key}-${p.code}`}
          onClick={() => toggle(p.code)}
          className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
            assigned.has(p.code)
              ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent"
              : "text-muted-foreground border-dashed hover:border-solid"}`}>
          {p.name}
        </button>
      ))}
    </div>
  );
};

const TRIGGER_ICON_LABEL = { payment_verified: "Payment", signup: "Signup",
  consultation_attended: "Consultation", case_created: "Process" };

const RuleEditorDialog = ({ open, setOpen, dataset, products, ruleMeta, editing, onSaved }) => {
  const emptyDraft = () => ({
    dataset_key: dataset?.dataset_key, name: "", product_codes: [],
    trigger: ruleMeta?.triggers?.[0]?.key || "payment_verified",
    event_type: "standard", event_name: "",
    customer_fields: ["phone", "email", "external_id"],
    value_source: "current_payment", fixed_value: null, currency: "INR",
    custom_fields: [], enabled: true,
  });
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDraft(editing ? {
      dataset_key: editing.dataset_key, name: editing.name,
      product_codes: editing.product_codes || [], trigger: editing.trigger,
      event_type: editing.event_type, event_name: editing.event_name,
      customer_fields: editing.customer_fields || [],
      value_source: editing.value_source || "current_payment",
      fixed_value: editing.fixed_value, currency: editing.currency || "INR",
      custom_fields: editing.custom_fields || [], enabled: editing.enabled !== false,
    } : emptyDraft());
  }, [open, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ruleMeta) return null;
  const triggerMeta = ruleMeta.triggers.find((t) => t.key === draft.trigger);
  const assignedProducts = products.filter((p) =>
    (dataset?.product_codes || []).includes(p.code) || dataset?.product_code === p.code);
  const toggleArr = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const save = async () => {
    if (!draft.product_codes.length) { toast.error("Choose at least one Product"); return; }
    if (!draft.event_name.trim()) { toast.error("Choose or enter an Event Name"); return; }
    setSaving(true);
    try {
      if (editing) await api.patch(`/meta/rules/${editing.rule_id}`, draft);
      else await api.post("/meta/rules", draft);
      toast.success(editing ? "Rule updated" : "Rule created");
      setOpen(false); onSaved();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="rule-editor-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} conversion rule — {dataset?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1">
            <Label className="text-xs">Rule name</Label>
            <Input data-testid="rule-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={triggerMeta?.label || "Consultation Attended"} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Product{assignedProducts.length > 1 ? "(s)" : ""} — from this dataset's assigned products</Label>
            <div className="flex flex-wrap gap-1.5">
              {assignedProducts.map((p) => (
                <button key={p.code} type="button" data-testid={`rule-product-${p.code}`}
                  onClick={() => setDraft({ ...draft, product_codes: toggleArr(draft.product_codes, p.code) })}
                  className={`text-[11px] px-2 py-1 rounded-md border ${
                    draft.product_codes.includes(p.code)
                      ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent"
                      : "text-muted-foreground"}`}>
                  {p.name}
                </button>
              ))}
              {assignedProducts.length === 0 && (
                <p className="text-[11px] text-[hsl(var(--warning))]">This dataset has no assigned products yet — assign one above first.</p>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Trigger</Label>
            <Select value={draft.trigger} onValueChange={(v) => setDraft({ ...draft, trigger: v, custom_fields: [] })}>
              <SelectTrigger data-testid="rule-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{ruleMeta.triggers.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Meta event type</Label>
              <Select value={draft.event_type} onValueChange={(v) => setDraft({ ...draft, event_type: v, event_name: "" })}>
                <SelectTrigger data-testid="rule-event-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard event</SelectItem>
                  <SelectItem value="custom">Custom event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Event name</Label>
              {draft.event_type === "standard" ? (
                <Select value={draft.event_name} onValueChange={(v) => setDraft({ ...draft, event_name: v })}>
                  <SelectTrigger data-testid="rule-event-name-select"><SelectValue placeholder="Pick event" /></SelectTrigger>
                  <SelectContent>{ruleMeta.standard_events.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid="rule-event-name-custom" value={draft.event_name}
                  onChange={(e) => setDraft({ ...draft, event_name: e.target.value })}
                  placeholder="ConsultationAttended" />
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Customer data to include</Label>
            <div className="grid grid-cols-2 gap-1">
              {ruleMeta.customer_fields.map((f) => (
                <label key={f.key} className="flex items-center gap-1.5 text-xs" data-testid={`rule-customer-field-${f.key}`}>
                  <Checkbox checked={draft.customer_fields.includes(f.key)}
                    onCheckedChange={() => setDraft({ ...draft, customer_fields: toggleArr(draft.customer_fields, f.key) })} />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
          {triggerMeta?.supports_payment_data && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Value source</Label>
                  <Select value={draft.value_source} onValueChange={(v) => setDraft({ ...draft, value_source: v })}>
                    <SelectTrigger data-testid="rule-value-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{ruleMeta.value_sources.map((v) => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {draft.value_source === "fixed" ? (
                  <div className="space-y-1"><Label className="text-xs">Fixed value</Label>
                    <Input data-testid="rule-fixed-value" type="number" value={draft.fixed_value ?? ""}
                      onChange={(e) => setDraft({ ...draft, fixed_value: e.target.value ? Number(e.target.value) : null })} /></div>
                ) : (
                  <div className="space-y-1"><Label className="text-xs">Currency</Label>
                    <Input data-testid="rule-currency" value={draft.currency}
                      onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })} /></div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Payment / commercial data to include</Label>
                <div className="grid grid-cols-2 gap-1">
                  {ruleMeta.payment_custom_fields.map((f) => (
                    <label key={f.key} className="flex items-center gap-1.5 text-xs" data-testid={`rule-payment-field-${f.key}`}>
                      <Checkbox checked={draft.custom_fields.includes(f.key)}
                        onCheckedChange={() => setDraft({ ...draft, custom_fields: toggleArr(draft.custom_fields, f.key) })} />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
          {!triggerMeta?.supports_payment_data && triggerMeta?.context_fields?.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Event context to include</Label>
              <div className="grid grid-cols-2 gap-1">
                {triggerMeta.context_fields.map((f) => (
                  <label key={f.key} className="flex items-center gap-1.5 text-xs" data-testid={`rule-context-field-${f.key}`}>
                    <Checkbox checked={draft.custom_fields.includes(f.key)}
                      onCheckedChange={() => setDraft({ ...draft, custom_fields: toggleArr(draft.custom_fields, f.key) })} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Switch data-testid="rule-enabled" checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} />
            <span className="text-xs">{draft.enabled ? "Enabled" : "Disabled"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rule-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save rule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RuleEventsDrawer = ({ rule, open, setOpen }) => {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    if (open && rule) {
      api.get(`/meta/rules/${rule.rule_id}/events`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
    }
  }, [open, rule]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="rule-events-drawer">
        <DialogHeader><DialogTitle className="font-heading">Recent events — {rule?.name}</DialogTitle></DialogHeader>
        {!events ? <p className="text-xs text-muted-foreground">Loading…</p> : events.length === 0 ? (
          <p className="text-xs text-muted-foreground">No delivery attempts yet for this rule.</p>
        ) : (
          <div className="space-y-1.5 text-xs">
            {events.map((e) => (
              <div key={e.outbox_id} className="rounded-md border px-2 py-1.5 flex items-center gap-2" data-testid={`rule-event-${e.outbox_id}`}>
                {e.status === "sent" ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))] shrink-0" />
                  : e.status?.startsWith("skipped") ? <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0" />
                  : e.status === "failed" ? <XCircle className="h-3.5 w-3.5 text-[hsl(var(--danger))] shrink-0" />
                  : <RefreshCw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                <span className="flex-1">{fmtIST(e.created_at, { year: true })} · {e.status}{e.is_test_record ? " · TEST" : ""}</span>
                {e.last_error && <span className="text-[hsl(var(--danger))] truncate max-w-[12rem]">{e.last_error}</span>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ConversionRulesSection = ({ dataset, rules, products, ruleMeta, onReload }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eventsRule, setEventsRule] = useState(null);
  const toggleEnabled = async (r) => {
    try {
      await api.patch(`/meta/rules/${r.rule_id}/enabled`, { enabled: !r.enabled });
      onReload();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const archive = async (r) => {
    try { await api.delete(`/meta/rules/${r.rule_id}`); toast.success(`'${r.name}' archived — its delivery history is kept`); onReload(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div className="pt-2 border-t space-y-1.5" data-testid={`rules-section-${dataset.dataset_key}`}>
      <p className="text-xs font-medium flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Conversion Rules ({rules.length})</p>
      {rules.map((r) => (
        <div key={r.rule_id} className="rounded-md border px-2.5 py-1.5 space-y-1 text-xs" data-testid={`rule-row-${r.rule_id}`}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium">{r.name}</span>
            <Badge variant="outline" className="text-[9px]">{TRIGGER_ICON_LABEL[r.trigger] || r.trigger}</Badge>
            <Badge variant="outline" className="text-[9px] font-mono">{r.event_name}</Badge>
            <Badge variant="outline" className="text-[9px]">{r.product_codes?.join(", ")}</Badge>
            {r.product_mismatch && (
              <Badge variant="outline" className="text-[9px] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30" title={r.product_mismatch_reason}>
                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />product mismatch
              </Badge>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Switch data-testid={`rule-toggle-${r.rule_id}`} checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} />
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" data-testid={`rule-edit-${r.rule_id}`}
                onClick={() => { setEditing(r); setEditorOpen(true); }}><Pencil className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" data-testid={`rule-events-${r.rule_id}`}
                onClick={() => setEventsRule(r)}><History className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-[hsl(var(--danger))]" data-testid={`rule-archive-${r.rule_id}`}
                onClick={() => archive(r)}><Archive className="h-3 w-3" /></Button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Last triggered: {r.stats?.last_triggered_at ? fmtIST(r.stats.last_triggered_at, { year: true }) : "never"} ·
            Last success: {r.stats?.last_success_at ? fmtIST(r.stats.last_success_at, { year: true }) : "never"} ·
            {r.stats?.success_count || 0} successful event{r.stats?.success_count === 1 ? "" : "s"}
            {r.stats?.last_error && <span className="text-[hsl(var(--danger))]"> · last error: {r.stats.last_error}</span>}
          </p>
        </div>
      ))}
      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`add-rule-${dataset.dataset_key}`}
        onClick={() => { setEditing(null); setEditorOpen(true); }}>
        <Plus className="h-3 w-3 mr-1" /> Add conversion rule
      </Button>
      <RuleEditorDialog open={editorOpen} setOpen={setEditorOpen} dataset={dataset} products={products}
        ruleMeta={ruleMeta} editing={editing} onSaved={onReload} />
      <RuleEventsDrawer rule={eventsRule} open={!!eventsRule} setOpen={(o) => !o && setEventsRule(null)} />
    </div>
  );
};

/* Missed-lead catch-up (2026-09 lead-intake audit, section E) — bounded,
   dry-run-first recovery for real Meta leads that arrived before native
   inbound was enabled/healthy, using the same Meta Lead Retrieval API
   "Fetch test lead" above already proves works. Every candidate lead goes
   through the exact same canonical webhook-processing path, so replaying
   this is always safe. */
const MetaBackfillDialog = ({ formKey, open, onOpenChange }) => {
  const [fromDatetime, setFromDatetime] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFromDatetime(""); setPreview(null); };
  const runPreview = async () => {
    if (!fromDatetime) { toast.error("Choose a from date/time first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/meta/forms/${formKey}/backfill/preview`, { from_datetime: fromDatetime });
      setPreview(data);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const runExecute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/meta/forms/${formKey}/backfill/execute`, { from_datetime: fromDatetime, confirm: true });
      toast.success(`Catch-up complete: ${data.created} created, ${data.attached} attached, ${data.duplicates} duplicates, ${data.errors.length} errors`);
      reset(); onOpenChange(false);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Missed-lead catch-up</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            Recovers real leads this form received before native inbound was enabled/healthy, via
            Meta's own Lead Retrieval API. Bounded by the date/time below — nothing earlier is ever
            touched. Every lead still goes through the same canonical intake/dedup path as a live
            webhook delivery — replaying this is always safe.
          </p>
          <div>
            <Label className="text-xs">From date/time (required)</Label>
            <Input type="datetime-local" value={fromDatetime} onChange={(e) => setFromDatetime(e.target.value)} />
          </div>
          {preview && !preview.error && (
            <div className="rounded-md border p-3 space-y-1 bg-muted/30" data-testid="meta-backfill-preview">
              <p className="text-xs font-medium">Preview — nothing created yet</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><p className="text-muted-foreground">Examined</p><p className="font-semibold">{preview.leads_examined}</p></div>
                <div><p className="text-muted-foreground">In range</p><p className="font-semibold">{preview.in_range}</p></div>
                <div><p className="text-muted-foreground">Would process</p><p className="font-semibold text-[hsl(var(--success))]">{preview.created}</p></div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" disabled={busy} onClick={runPreview}>{busy ? "Working…" : "Preview"}</Button>
          <Button size="sm" disabled={busy || !preview || preview.error} onClick={runExecute} data-testid="meta-backfill-execute">
            {busy ? "Working…" : "Execute — Admin confirms"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MetaAdminTab = () => {
  const [conn, setConn] = useState(null);
  const [creds, setCreds] = useState({ access_token: "", verify_token: "", app_secret: "", app_id: "" });
  // Per-field dirty/touched tracking — a save only ever submits a field the
  // user actually edited in THIS input, never whatever happens to be
  // sitting (non-empty) in an unrelated field. Root cause of a prior
  // incident: saving app_id resubmitted a leftover value in Access Token
  // too, silently overwriting the real stored token.
  const [touched, setTouched] = useState({ access_token: false, verify_token: false, app_secret: false, app_id: false });
  const setCred = (k, v) => { setCreds((c) => ({ ...c, [k]: v })); setTouched((t) => ({ ...t, [k]: true })); };
  const [pageSubs, setPageSubs] = useState({});   // page_id -> subscription status result
  const [subBusy, setSubBusy] = useState({});     // page_id -> "checking" | "subscribing" | "unsubscribing"
  const [confirmUnsubPage, setConfirmUnsubPage] = useState(null); // page object pending confirmation
  const [datasets, setDatasets] = useState([]);
  const [forms, setForms] = useState([]);
  const [products, setProducts] = useState([]);
  const [outboxStatus, setOutboxStatus] = useState(null);
  const [confirmEnable, setConfirmEnable] = useState(false);
  const [backfillForm, setBackfillForm] = useState(null); // form_key with catch-up dialog open
  const [nd, setNd] = useState({ name: "", dataset_id: "", product_code: "none", test_event_code: "" });
  const [nf, setNf] = useState({ name: "", form_id: "", page_name: "", product_code: "none", mapping: "full_name=name\nphone_number=phone\nemail=email" });
  const [sample, setSample] = useState(null);
  const [logs, setLogs] = useState({ inbound: [], outbound: [] });
  const [assets, setAssets] = useState(null);
  const [pageSel, setPageSel] = useState("");
  const [formSel, setFormSel] = useState("");
  const [mapperForm, setMapperForm] = useState(null); // form being visually mapped
  const [mapRows, setMapRows] = useState([]);         // [{meta_field, target}]
  const [ruleMeta, setRuleMeta] = useState(null);
  const [rulesByDataset, setRulesByDataset] = useState({});

  const load = useCallback(() => {
    api.get("/meta/connection").then((r) => setConn(r.data)).catch(() => {});
    api.get("/meta/datasets").then((r) => setDatasets(r.data)).catch(() => {});
    api.get("/meta/forms").then((r) => setForms(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/meta/logs?limit=10").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/meta/outbox-status").then((r) => setOutboxStatus(r.data)).catch(() => {});
    api.get("/meta/rule-meta").then((r) => setRuleMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadRules = useCallback((datasetKey) => {
    api.get(`/meta/datasets/${datasetKey}/rules`)
      .then((r) => setRulesByDataset((prev) => ({ ...prev, [datasetKey]: r.data })))
      .catch(() => {});
  }, []);
  useEffect(() => { datasets.forEach((d) => loadRules(d.dataset_key)); }, [datasets, loadRules]);

  const saveConn = async (extra = {}) => {
    try {
      const body = { ...extra };
      // Only a field the user actually touched in this session gets sent —
      // an untouched field is never resubmitted even if it happens to hold
      // leftover text, and a blank field always preserves what's stored.
      for (const k of ["access_token", "verify_token", "app_secret", "app_id"])
        if (touched[k] && creds[k]) body[k] = creds[k];
      await api.put("/meta/connection", body);
      toast.success("Meta configuration saved (secrets stay server-side)");
      setCreds({ access_token: "", verify_token: "", app_secret: "", app_id: "" });
      setTouched({ access_token: false, verify_token: false, app_secret: false, app_id: false });
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  // Turning outbound ON while there's a backlog of real (non-test) events
  // already stuck as skipped_provider_off needs a deliberate acknowledgment
  // — they will NOT auto-send just because the switch flips (the outbox
  // only claims status=pending; skipped items need an explicit retry).
  const handleConversionsToggle = (v) => {
    if (v && (outboxStatus?.blocked_real_count || 0) > 0) { setConfirmEnable(true); return; }
    saveConn({ conversions_enabled: v });
  };
  const confirmEnableConversions = () => { setConfirmEnable(false); saveConn({ conversions_enabled: true }); };
  const testConn = async () => {
    try {
      const r = await api.post("/meta/connection/test");
      if (r.data.ok) { toast.success(`Connected: ${r.data.account?.name}`); return; }
      const detail = [r.data.http_status && `HTTP ${r.data.http_status}`, r.data.error_category?.replace(/_/g, " ")].filter(Boolean).join(" · ");
      toast.error(r.data.message || "Not connected", detail ? { description: detail } : undefined);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const addDataset = async () => {
    try {
      await api.post("/meta/datasets", {
        ...nd, product_code: nd.product_code === "none" ? null : nd.product_code,
        test_event_code: nd.test_event_code || null, enabled: true, test_mode: true,
      });
      toast.success("Dataset added (TEST mode)");
      setNd({ name: "", dataset_id: "", product_code: "none", test_event_code: "" }); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const addForm = async () => {
    try {
      const mapping = {};
      nf.mapping.split("\n").forEach((l) => { const [k, v] = l.split("=").map((x) => x?.trim()); if (k && v) mapping[k] = v; });
      await api.post("/meta/forms", { name: nf.name, form_id: nf.form_id, page_name: nf.page_name, page_id: "", product_code: nf.product_code === "none" ? null : nf.product_code, mapping, duplicate_mode: "attach", enabled: true });
      toast.success("Lead form registered"); setNf({ ...nf, name: "", form_id: "" }); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const fetchLead = async (fk) => {
    try { const r = await api.post(`/meta/forms/${fk}/fetch-test-lead`); setSample(r.data); toast[r.data.mode === "mock" ? "info" : "success"](r.data.message || `Fetched ${r.data.fields.length} fields (${r.data.mode})`); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const loadAssets = async () => {
    try {
      const r = await api.get("/meta/assets");
      setAssets(r.data);
      if (r.data.ok === false) {
        // A genuine Meta-side failure — never shown as "Loaded 0 page(s)".
        toast.error(r.data.message || "Failed to load Pages from Meta");
      } else {
        toast[r.data.mode === "mock" ? "info" : "success"](r.data.message || `Loaded ${r.data.pages?.length || 0} page(s) from Meta`);
      }
    } catch (e) { toast.error(errDetail(e)); }
  };
  // Page-level Lead Ads webhook subscription (POST/GET/DELETE
  // /{page_id}/subscribed_apps) — read-only "check" happens on demand only
  // (never automatically), and Subscribe/Unsubscribe are explicit clicks.
  const checkPageSubscription = async (pageId) => {
    setSubBusy((b) => ({ ...b, [pageId]: "checking" }));
    try {
      const r = await api.get(`/meta/pages/${pageId}/subscription`);
      setPageSubs((s) => ({ ...s, [pageId]: r.data }));
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSubBusy((b) => ({ ...b, [pageId]: null })); }
  };
  const subscribePage = async (pageId) => {
    setSubBusy((b) => ({ ...b, [pageId]: "subscribing" }));
    try {
      const r = await api.post(`/meta/pages/${pageId}/subscription`);
      setPageSubs((s) => ({ ...s, [pageId]: r.data }));
      toast[r.data.leadgen_subscribed ? "success" : "error"](r.data.message);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSubBusy((b) => ({ ...b, [pageId]: null })); }
  };
  const unsubscribePage = async (pageId) => {
    setConfirmUnsubPage(null);
    setSubBusy((b) => ({ ...b, [pageId]: "unsubscribing" }));
    try {
      const r = await api.delete(`/meta/pages/${pageId}/subscription`);
      setPageSubs((s) => ({ ...s, [pageId]: r.data }));
      toast.success("SalesOS Integration app unsubscribed from this Page — Pabbly/Buffer/Zoho Social are untouched");
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSubBusy((b) => ({ ...b, [pageId]: null })); }
  };
  const pickForm = (pageId, formId) => {
    const page = (assets?.pages || []).find((p) => p.page_id === pageId);
    const fm = (page?.forms || []).find((f) => f.id === formId);
    if (page && fm) setNf({ ...nf, name: fm.name, form_id: fm.id, page_name: page.name });
  };
  const META_UNIVERSAL_TARGETS = [
    ["name", "Contact — Full name"], ["phone", "Contact — Phone"], ["email", "Contact — Email"],
    ["city", "Contact — City"], ["source", "Attribution — Source"], ["campaign", "Attribution — Campaign"],
  ];
  const openMapper = async (f) => {
    try {
      const r = await api.post(`/meta/forms/${f.form_key}/fetch-test-lead`);
      setSample(r.data);
      const rows = (r.data.fields || []).map((fl) => ({
        key: fl.name, label: fl.name,
        sample: Array.isArray(fl.values) ? fl.values[0] : fl.values,
        target: (f.mapping || {})[fl.name] || "skip",
      }));
      // keep existing mapping entries not present in the sample
      Object.entries(f.mapping || {}).forEach(([k, v]) => {
        if (!rows.some((x) => x.key === k)) rows.push({ key: k, label: k, sample: "", target: v });
      });
      setMapRows(rows); setMapperForm(f);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const saveMapping = async () => {
    try {
      const mapping = {};
      mapRows.forEach((r) => { if (r.target && r.target !== "skip") mapping[r.key] = r.target; });
      await api.patch(`/meta/forms/${mapperForm.form_key}`, {
        name: mapperForm.name, form_id: mapperForm.form_id, page_id: mapperForm.page_id || "",
        page_name: mapperForm.page_name || "", product_code: mapperForm.product_code || null,
        mapping, duplicate_mode: mapperForm.duplicate_mode || "attach", enabled: mapperForm.enabled !== false,
      });
      toast.success("Field mapping saved");
      setMapperForm(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const toggleDatasetTestMode = async (d) => {
    try {
      await api.patch(`/meta/datasets/${d.dataset_key}`, {
        name: d.name, dataset_id: d.dataset_id, product_code: d.product_code || null,
        enabled: d.enabled !== false, test_mode: !d.test_mode,
      });
      toast.success(d.test_mode
        ? "Dataset switched to LIVE — its real events now count for real ad optimization"
        : "Dataset switched back to TEST mode");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const saveDatasetTestCode = async (d, code) => {
    try {
      await api.patch(`/meta/datasets/${d.dataset_key}`, {
        name: d.name, dataset_id: d.dataset_id, product_code: d.product_code || null,
        enabled: d.enabled !== false, test_mode: d.test_mode !== false,
        test_event_code: code || null,
      });
      toast.success("Test event code saved for this dataset");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const testEvent = async (dk) => {
    try { const r = await api.post("/meta/test-event", { dataset_key: dk, event_name: "Lead" }); toast[r.data.sent ? "success" : "info"](`${r.data.mode}: ${r.data.sent ? "sent" : (r.data.reason || "simulated")}`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  if (!conn) return <div className="p-4 text-sm text-muted-foreground">Loading Meta configuration…</div>;
  return (
    <div className="grid gap-4 md:grid-cols-2 pt-2" data-testid="admin-meta-tab">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Facebook className="h-4 w-4 text-primary" />Connection & kill switches</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="text-xs text-muted-foreground">Status: {conn.token_configured ? <Badge className="bg-emerald-500/15 text-emerald-600">token configured</Badge> : <Badge variant="outline">awaiting credentials</Badge>} {conn.account_name && <span className="ml-1">({conn.account_name})</span>}</div>
          <Row label="Meta connection (master switch)">
            <Switch data-testid="meta-switch-connection_enabled" checked={!!conn.switches.connection_enabled} onCheckedChange={(v) => saveConn({ connection_enabled: v })} />
          </Row>
          <p className="text-[11px] text-muted-foreground">Master switch for everything below — turn this off and neither child switch works, no matter its own setting.</p>
          {(outboxStatus?.blocked_real_count || 0) > 0 && !conn.switches.conversions_enabled && (
            <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-2.5" data-testid="meta-outbox-warning">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-medium">{outboxStatus.blocked_real_count} real (non-test) conversion event{outboxStatus.blocked_real_count === 1 ? "" : "s"} already queued and blocked</p>
                <p className="text-muted-foreground mt-0.5">Turning outbound on will <b>not</b> auto-send these — they stay frozen until explicitly retried from the Integration outbox. See what's queued below.</p>
              </div>
            </div>
          )}
          <div className={`space-y-1 rounded-md border pl-2 ${!conn.switches.connection_enabled ? "opacity-50" : ""}`}>
            <Row label="Send conversion events to Meta (outbound)">
              <Switch data-testid="meta-switch-conversions_enabled" checked={!!conn.switches.conversions_enabled} disabled={!conn.switches.connection_enabled} onCheckedChange={handleConversionsToggle} />
            </Row>
            <Row label="Lead intake (inbound)">
              <Switch data-testid="meta-switch-lead_intake_enabled" checked={!!conn.switches.lead_intake_enabled} disabled={!conn.switches.connection_enabled} onCheckedChange={(v) => saveConn({ lead_intake_enabled: v })} />
            </Row>
          </div>
          <p className="text-[11px] text-muted-foreground">
            <b>Send conversion events to Meta</b> is what actually gates Lead/ConsultationBooked/ConsultationAttended/custom-automation events reaching Meta (the old toggle on Admin → Integrations is gone — this replaces it). TEST customer records never send regardless of any switch here — that's a separate, unrelated protection (Admin → Integrations → "Allow TEST records to trigger real sends", off by default). Whether a specific destination is live is controlled <b>only</b> in the Dataset manager below — each dataset has its own TEST/LIVE toggle and its own Test Event Code (Meta issues a different code per dataset). There's no separate account-wide TEST switch any more, so there's exactly one place that decides it.
          </p>
          <div className={`rounded-md border p-2.5 space-y-1.5 ${conn.switches.inbound_test_mode ? "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10" : ""}`} data-testid="meta-inbound-test-mode-card">
            <Row label="Inbound testing mode (Lead intake)">
              <Switch data-testid="meta-switch-inbound_test_mode" checked={!!conn.switches.inbound_test_mode} onCheckedChange={(v) => saveConn({ inbound_test_mode: v })} />
            </Row>
            <p className="text-[11px] text-muted-foreground">
              Independent of every other switch here — the master switch, Lead intake on/off, outbound conversions, dataset TEST/LIVE, and the shared "Allow TEST records to trigger real sends" protection all have zero effect on this. It controls exactly one thing: whether a newly received Meta lead is marked a SalesOS TEST record.
            </p>
            {conn.switches.inbound_test_mode ? (
              <p className="text-xs font-medium text-[hsl(var(--warning))]" data-testid="meta-inbound-test-mode-warning">
                Inbound testing mode — incoming Meta leads will be marked TEST and excluded from normal reporting. Turn OFF before receiving real leads.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">OFF — newly received Meta leads are normal production records. Turn this ON only while you're deliberately using Meta's own Lead Ads Testing Tool.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div><Label className="text-xs">Access token{touched.access_token && <span className="text-[hsl(var(--info))]"> · edited</span>}</Label><Input data-testid="meta-token" type="password" autoComplete="new-password" placeholder={conn.token_configured ? "•••• configured" : "paste token"} value={creds.access_token} onChange={(e) => setCred("access_token", e.target.value)} /></div>
            <div><Label className="text-xs">Webhook verify token{touched.verify_token && <span className="text-[hsl(var(--info))]"> · edited</span>}</Label><Input data-testid="meta-verify" type="password" autoComplete="new-password" placeholder={conn.verify_token_configured ? "•••• configured" : "choose a token"} value={creds.verify_token} onChange={(e) => setCred("verify_token", e.target.value)} /></div>
            <div><Label className="text-xs">App secret{touched.app_secret && <span className="text-[hsl(var(--info))]"> · edited</span>}</Label><Input type="password" autoComplete="new-password" placeholder={conn.app_secret_configured ? "•••• configured" : "optional"} value={creds.app_secret} onChange={(e) => setCred("app_secret", e.target.value)} /></div>
            <div><Label className="text-xs">App ID (SalesOS Integration app, not secret)</Label><Input data-testid="meta-app-id" autoComplete="off" placeholder={conn.app_id || "e.g. 1840394580460750"} value={creds.app_id} onChange={(e) => setCred("app_id", e.target.value)} /></div>
          </div>
          <p className="text-[10px] text-muted-foreground">Each field above is saved independently — only ones marked "edited" are sent, so e.g. updating App ID alone can never touch the stored Access Token.</p>
          <div className="flex gap-2 pt-1">
            <Button size="sm" data-testid="meta-save" onClick={() => saveConn()}>Save credentials</Button>
            <Button size="sm" variant="outline" data-testid="meta-test-conn" onClick={testConn}>Test connection</Button>
          </div>
          <div className="text-xs text-muted-foreground pt-1">Inbound webhook URL: <code className="bg-muted px-1 rounded">{process.env.REACT_APP_BACKEND_URL}/api/hooks/meta</code> (also accepts Pabbly/n8n bridge payloads)</div>
          <div className="text-[11px] text-muted-foreground rounded-md bg-muted/60 p-2 space-y-1">
            <p className="font-medium text-foreground">Three separate things, easy to conflate:</p>
            <p><b>1. Webhook endpoint verified</b> — Meta's Developer Console knows <code className="font-mono">/api/hooks/meta</code> as a valid callback URL (App → Webhooks product, verified via the token above). Configured entirely in Meta's dashboard — SalesOS has no API to check or set this.</p>
            <p><b>2. App subscribed to the leadgen field</b> — the App's own Webhooks product declares it wants <code className="font-mono">leadgen</code> updates at all. Also Developer-Console-only, not per-Page, not visible via this Graph API.</p>
            <p><b>3. A specific Page subscribed to the SalesOS Integration app</b> — <code className="font-mono">POST /&#123;page_id&#125;/subscribed_apps</code>. This is the only one SalesOS can check or control — see Lead forms &amp; field mapping below, per Page.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Database className="h-4 w-4 text-primary" />Dataset / destination manager</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">Multiple offline-event datasets with product mapping. Missing product mapping is skipped — never silently rerouted. While a dataset is TEST, its automatic real-customer conversions are never sent to Meta at all — they're held as <code className="font-mono">skipped_dataset_test</code> in the outbox below and need an explicit retry once you switch it to LIVE (switching does not auto-release them). Test Event Code only matters for the manual "Send test event" button, which always sends as test regardless of the TEST/LIVE toggle.</p>
          {datasets.map((d) => {
            const heldForThisDataset = (outboxStatus?.items || []).filter(
              (it) => it.status === "skipped_dataset_test" && it.dataset_name === d.name).length;
            return (
            <div key={d.dataset_key} className="rounded-md border px-3 py-2 text-sm space-y-1.5" data-testid={`dataset-${d.dataset_key}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{d.name}</span>
                <Badge variant="outline">{d.dataset_id}</Badge>
                {d.product_code && <Badge>{d.product_code}</Badge>}
                <Button size="sm" variant="outline"
                  className={`h-6 text-[10px] px-2 ${d.test_mode ? "text-violet-600 border-violet-300" : "text-emerald-600 border-emerald-300"}`}
                  data-testid={`dataset-mode-${d.dataset_key}`} onClick={() => toggleDatasetTestMode(d)}>
                  {d.test_mode ? "TEST — click to go live" : "LIVE"}
                </Button>
                {!d.enabled && <Badge variant="destructive">disabled</Badge>}
                <Button size="sm" variant="ghost" className="ml-auto h-7" data-testid={`dataset-test-${d.dataset_key}`} onClick={() => testEvent(d.dataset_key)}><Send className="h-3.5 w-3.5 mr-1" />Send test event</Button>
              </div>
              {d.test_mode && heldForThisDataset > 0 && (
                <p className="text-[10px] text-[hsl(var(--warning))]" data-testid={`dataset-held-${d.dataset_key}`}>
                  {heldForThisDataset} real conversion{heldForThisDataset === 1 ? "" : "s"} withheld while this dataset is TEST — none sent to Meta, none lost.
                </p>
              )}
              <div className="flex items-center gap-1.5">
                <Label className="text-[10px] text-muted-foreground shrink-0">Test event code (manual "Send test event" only)</Label>
                <Input data-testid={`dataset-testcode-${d.dataset_key}`}
                  defaultValue={d.test_event_code || ""}
                  placeholder="TESTxxxxx"
                  className="h-6 w-40 text-[10px] font-mono"
                  onBlur={(e) => { if (e.target.value.trim() !== (d.test_event_code || "")) saveDatasetTestCode(d, e.target.value.trim()); }} />
                {!d.test_event_code && (
                  <span className="text-[10px] text-muted-foreground">no code set — "Send test event" will simulate only</span>
                )}
              </div>
              <DatasetProductsEditor dataset={d} products={products} onSaved={load} />
              <ConversionRulesSection dataset={d} rules={rulesByDataset[d.dataset_key] || []}
                products={products} ruleMeta={ruleMeta} onReload={() => loadRules(d.dataset_key)} />
            </div>
            );
          })}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Input data-testid="dataset-name" placeholder="Friendly name" value={nd.name} onChange={(e) => setNd({ ...nd, name: e.target.value })} />
            <Input data-testid="dataset-id" placeholder="Dataset ID" value={nd.dataset_id} onChange={(e) => setNd({ ...nd, dataset_id: e.target.value })} />
            <Select value={nd.product_code} onValueChange={(v) => setNd({ ...nd, product_code: v })}>
              <SelectTrigger data-testid="dataset-product"><SelectValue placeholder="Product map" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No product mapping</SelectItem>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="dataset-testcode-new" placeholder="Test event code (optional)" value={nd.test_event_code} onChange={(e) => setNd({ ...nd, test_event_code: e.target.value })} />
          </div>
          <Button size="sm" data-testid="dataset-add" onClick={addDataset} disabled={!nd.name || !nd.dataset_id}>Add dataset</Button>

          {outboxStatus?.items?.length > 0 && (
            <div className="pt-2 border-t space-y-1.5">
              <p className="text-xs font-medium">Queued / blocked conversion events ({outboxStatus.items.length})</p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {outboxStatus.items.map((it) => (
                  <div key={it.outbox_id} className="rounded border px-2 py-1 text-[11px] flex flex-wrap items-center gap-1.5" data-testid={`meta-outbox-item-${it.outbox_id}`}>
                    <Badge variant="outline" className="text-[9px]">{it.status}</Badge>
                    <span className="font-medium">{it.event_name}</span>
                    <span className="text-muted-foreground">→ {it.dataset_name || it.product_code || it.destination || "unmapped"}</span>
                    {it.is_test_record && <Badge className="text-[9px] bg-violet-500/15 text-violet-600">SalesOS TEST record</Badge>}
                    <span className="text-muted-foreground ml-auto">{fmtIST(it.created_at)}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">{outboxStatus.note}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FormInput className="h-4 w-4 text-primary" />Lead forms & field mapping</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid="meta-load-assets" onClick={loadAssets}><Download className="h-3.5 w-3.5 mr-1" />Load Pages &amp; Forms from Meta</Button>
            {assets?.mode === "mock" && <Badge className="bg-violet-500/15 text-violet-600">MOCK until connected</Badge>}
          </div>
          {assets?.ok === false && assets.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs space-y-1" data-testid="meta-assets-error">
              <p className="font-medium text-destructive">Could not load Pages from Meta — {(assets.error.category || "error").replace(/_/g, " ")}</p>
              <p className="text-muted-foreground">{assets.message}</p>
              {(assets.error.http_status || assets.error.code) && (
                <p className="text-muted-foreground font-mono text-[10px]">HTTP {assets.error.http_status ?? "—"} · code {assets.error.code ?? "—"} · {assets.error.type || "—"}</p>
              )}
              <p className="text-muted-foreground">This is distinct from a genuine "0 Pages" result — nothing was cleared or changed locally; existing form registrations and mappings below are untouched.</p>
            </div>
          )}
          {assets?.ok !== false && (assets?.page_errors?.length > 0) && (
            <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-2 text-xs space-y-0.5" data-testid="meta-page-errors">
              <p className="font-medium">Some Pages loaded but their forms couldn't be fetched:</p>
              {assets.page_errors.map((pe) => <p key={pe.page_id} className="text-muted-foreground">{pe.name || pe.page_id}: {pe.message || `HTTP ${pe.http_status}`}</p>)}
            </div>
          )}
          {assets && (assets.pages || []).length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium">Page → SalesOS Integration app subscription</p>
              <p className="text-[11px] text-muted-foreground">Whether each Page has actually subscribed the SalesOS Integration app to its <code className="font-mono">leadgen</code> field — the one thing that makes leads actually flow. Checked only when you click, never automatically. Subscribing/unsubscribing here never touches Pabbly, Buffer, or Zoho Social's own subscriptions.</p>
              {(assets.pages || []).map((p) => {
                const sub = pageSubs[p.page_id];
                const busy = subBusy[p.page_id];
                return (
                  <div key={p.page_id} className="rounded-md border px-2 py-1.5 text-xs flex flex-wrap items-center gap-2" data-testid={`page-sub-${p.page_id}`}>
                    <span className="font-medium">{p.name}</span>
                    {!sub && <Badge variant="outline" className="text-[9px]">not checked yet</Badge>}
                    {sub?.status === "subscribed" && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-600">subscribed</Badge>}
                    {sub?.status === "not_subscribed" && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300">not subscribed</Badge>}
                    {sub && (sub.status === "error" || sub.status === "no_page_token") && (
                      <Badge variant="destructive" className="text-[9px]">status unknown — {sub.status === "no_page_token" ? "no Page token" : "Meta error"}</Badge>
                    )}
                    <div className="ml-auto flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" data-testid={`page-check-${p.page_id}`} disabled={!!busy} onClick={() => checkPageSubscription(p.page_id)}>
                        <RefreshCw className="h-3 w-3 mr-1" />{busy === "checking" ? "Checking…" : "Check status"}
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" data-testid={`page-subscribe-${p.page_id}`} disabled={!!busy} onClick={() => subscribePage(p.page_id)}>
                        <Link2 className="h-3 w-3 mr-1" />{busy === "subscribing" ? "Subscribing…" : "Subscribe"}
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-destructive" data-testid={`page-unsubscribe-${p.page_id}`} disabled={!!busy} onClick={() => setConfirmUnsubPage(p)}>
                        <Unlink className="h-3 w-3 mr-1" />{busy === "unsubscribing" ? "Unsubscribing…" : "Unsubscribe"}
                      </Button>
                    </div>
                    {sub?.message && <p className="w-full text-[10px] text-muted-foreground">{sub.message}</p>}
                  </div>
                );
              })}
            </div>
          )}
          {assets && (
            <div className="grid grid-cols-2 gap-2">
              <Select value={pageSel} onValueChange={(v) => { setPageSel(v); setFormSel(""); }}>
                <SelectTrigger data-testid="meta-page-select"><SelectValue placeholder="Select Facebook Page" /></SelectTrigger>
                <SelectContent>{(assets.pages || []).map((p) => <SelectItem key={p.page_id} value={p.page_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={formSel} onValueChange={(v) => { setFormSel(v); pickForm(pageSel, v); }} disabled={!pageSel}>
                <SelectTrigger data-testid="meta-form-select"><SelectValue placeholder="Select Lead Form" /></SelectTrigger>
                <SelectContent>{((assets.pages || []).find((p) => p.page_id === pageSel)?.forms || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {forms.map((f) => (
            <div key={f.form_key} className="rounded-md border px-3 py-2 text-sm" data-testid={`metaform-${f.form_key}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{f.name}</span>
                <Badge variant="outline">form {f.form_id}</Badge>
                {f.product_code && <Badge>{f.product_code}</Badge>}
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7" data-testid={`map-fields-${f.form_key}`} onClick={() => openMapper(f)}><FormInput className="h-3.5 w-3.5 mr-1" />Map fields</Button>
                  <Button size="sm" variant="ghost" className="h-7" data-testid={`fetch-lead-${f.form_key}`} onClick={() => fetchLead(f.form_key)}><Download className="h-3.5 w-3.5 mr-1" />Fetch test lead</Button>
                  <Button size="sm" variant="ghost" className="h-7" data-testid={`backfill-open-${f.form_key}`} onClick={() => setBackfillForm(f.form_key)}><History className="h-3.5 w-3.5 mr-1" />Missed-lead catch-up</Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Mapping: {Object.entries(f.mapping || {}).map(([k, v]) => `${k}→${v}`).join(", ") || "none"}</div>
              <div className="mt-2">
                <WebinarSourceBinding sourceType="meta_form" sourceId={f.form_key} defaultProduct={f.product_code} />
              </div>
              <MetaBackfillDialog formKey={f.form_key} open={backfillForm === f.form_key}
                onOpenChange={(v) => setBackfillForm(v ? f.form_key : null)} />
            </div>
          ))}
          {sample && !mapperForm && (
            <div className="rounded-md border border-dashed p-2 text-xs" data-testid="sample-lead">
              <div className="font-medium mb-1">Sample lead ({sample.mode}{sample.mode === "mock" ? " — synthetic until Meta is connected" : ""})</div>
              {(sample.fields || []).map((f) => <div key={f.name}>{f.name}: <b>{Array.isArray(f.values) ? f.values[0] : f.values}</b></div>)}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Input data-testid="form-name" placeholder="Form name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} />
            <Input data-testid="form-id" placeholder="Meta form ID" value={nf.form_id} onChange={(e) => setNf({ ...nf, form_id: e.target.value })} />
            <Input placeholder="Page name" value={nf.page_name} onChange={(e) => setNf({ ...nf, page_name: e.target.value })} />
            <Select value={nf.product_code} onValueChange={(v) => setNf({ ...nf, product_code: v })}>
              <SelectTrigger data-testid="form-product"><SelectValue placeholder="Product" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No product</SelectItem>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button size="sm" data-testid="form-add" onClick={addForm} disabled={!nf.name || !nf.form_id}>Register form</Button>
          <p className="text-[11px] text-muted-foreground">After registering, use <b>Map fields</b> for the visual Meta field → SalesOS field mapper (fetches real/test lead fields — no raw textarea needed).</p>
        </CardContent>
      </Card>

      <Dialog open={!!confirmUnsubPage} onOpenChange={(o) => !o && setConfirmUnsubPage(null)}>
        <DialogContent className="sm:max-w-md" data-testid="page-unsubscribe-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />Unsubscribe from {confirmUnsubPage?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm">This stops the SalesOS Integration app specifically from receiving leadgen events for this Page — new leads submitted on Meta will no longer reach <code className="font-mono">/api/hooks/meta</code>. This only affects the SalesOS Integration app; Pabbly, Buffer, and Zoho Social's own subscriptions to this Page are untouched.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmUnsubPage(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="page-unsubscribe-confirm" onClick={() => unsubscribePage(confirmUnsubPage.page_id)}>Unsubscribe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmEnable} onOpenChange={setConfirmEnable}>
        <DialogContent className="sm:max-w-md" data-testid="meta-enable-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />Pending events won't be released</DialogTitle></DialogHeader>
          <p className="text-sm">{outboxStatus?.blocked_real_count} real (non-test) conversion event{outboxStatus?.blocked_real_count === 1 ? "" : "s"} are already queued and currently blocked. Turning outbound on will <b>not</b> send them automatically — nothing is deleted or sent by this action either way. Only newly queued events will start flowing. If you want the existing backlog sent too, retry each item afterward from Admin → Integrations → Integration outbox.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmEnable(false)}>Cancel</Button>
            <Button data-testid="meta-enable-confirm" onClick={confirmEnableConversions}>Enable outbound anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mapperForm} onOpenChange={(o) => !o && setMapperForm(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="meta-mapper-dialog">
          <DialogHeader><DialogTitle className="font-heading">Field mapping — {mapperForm?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Meta question → SalesOS field. Nothing is mapped automatically — every question defaults to "Preserve only" until you choose a target. Unmapped questions stay fully visible under Lead Form Information on Customer 360.</p>
          <FieldMapper rows={mapRows} onChange={setMapRows}
            productCode={mapperForm?.product_code} universalTargets={META_UNIVERSAL_TARGETS} />
          <DialogFooter><Button data-testid="mapper-save" onClick={saveMapping}>Save mapping</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Logs (inbound & outbound)</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {(logs.inbound || []).map((l) => (
            <div key={l.log_id} className="rounded border px-2 py-1 flex flex-wrap gap-2" data-testid={`metalog-in-${l.log_id}`}>
              <Badge variant="outline">IN</Badge><span>{l.meta_lead_id}</span><span>{l.form_name || l.form_id}</span>
              <span className={l.status === "ok" ? "text-emerald-600" : "text-amber-600"}>{l.status}</span>
              <span className="text-muted-foreground ml-auto">{fmtIST(l.at)}</span>
            </div>
          ))}
          {(logs.outbound || []).map((l) => (
            <div key={l.log_id} className="rounded border px-2 py-1 flex flex-wrap gap-2" data-testid={`metalog-out-${l.log_id}`}>
              <Badge variant="outline">OUT</Badge><span>{l.event_name}</span><span>{l.dataset_name}</span>
              <Badge className={l.test ? "bg-violet-500/15 text-violet-600" : ""}>{l.test ? "TEST" : "LIVE"}</Badge>
              <span className={l.sent ? "text-emerald-600" : "text-muted-foreground"}>{l.sent ? "sent" : l.mode}</span>
              <span className="text-muted-foreground ml-auto">{fmtIST(l.at)}</span>
            </div>
          ))}
          {!(logs.inbound || []).length && !(logs.outbound || []).length && <div className="text-muted-foreground">No Meta activity yet.</div>}
        </CardContent>
      </Card>
    </div>
  );
};
